import { createFileRoute, redirect, useNavigate, useSearch } from '@tanstack/react-router'
import { z } from 'zod'

import { InboxPage } from '@/components/emails/inbox/inbox-page'
import { DEFAULT_INBOX_TAB, INBOX_TABS, type InboxTab } from '@/components/emails/inbox/inbox-utils'
import { requireOrganizationFeature } from '@/lib/route-preloading'
import { PermissionDeniedRouteError, requirePermission } from '@/lib/require-permission-guard'
import { OrganizationFeatureEnum } from '@/gen/cortado/v1/settings_pb'
import { useCallback } from 'react'
import { useStore } from '@tanstack/react-store'
import { organizationStore } from '@/stores/organization.store'

const INBOX_TAB_VALUES = INBOX_TABS.map((tab) => tab.value) as [InboxTab, ...InboxTab[]]

export const inboxSearchSchema = z.object({
  tab: z.enum(INBOX_TAB_VALUES).optional().catch(undefined),
  thread: z.string().optional().catch(undefined),
  mailbox: z.string().optional().catch(undefined),
  unread: z.boolean().optional().catch(undefined),
  q: z.string().optional().catch(undefined),
})

export type InboxSearch = z.infer<typeof inboxSearchSchema>

/**
 * The Inbox (CPRO-793): every conversation across the organization's
 * connected mailboxes. It needs the Connected inbox add-on and View
 * Communications; anyone else lands on the dashboard.
 */
export const Route = createFileRoute('/_app/_home/inbox')({
  component: InboxRoute,
  validateSearch: inboxSearchSchema,
  beforeLoad: async ({ context }) => {
    const toDashboard = () => redirect({ to: '/dashboard', replace: true })
    await requireOrganizationFeature(context, OrganizationFeatureEnum.ORGANIZATION_FEATURE_EMAIL_INBOX, toDashboard)
    await requirePermission(context.queryClient, 'CommsView').catch((error: unknown) => {
      throw error instanceof PermissionDeniedRouteError ? toDashboard() : error
    })
  },
})

function InboxRoute() {
  const navigate = useNavigate()
  const search = useSearch({ from: '/_app/_home/inbox' })
  const organizationId = useStore(organizationStore, (state) => state.selectedOrganizationId) || ''

  const onSearchChange = useCallback(
    (next: Partial<InboxSearch>) => navigate({ to: '/inbox', search: { ...search, ...next }, replace: true }),
    [navigate, search],
  )

  return (
    <InboxPage
      organizationId={organizationId}
      search={{ ...search, tab: search.tab ?? DEFAULT_INBOX_TAB }}
      onSearchChange={onSearchChange}
    />
  )
}
